import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import DataTable, { Column, DataTableSelectedData } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { getPeriodByAvailability } from '../../../api/periods/get-period-by-availability'
import { tripStepTypeMapping, tripTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripDate } from '../../../lib/types/models/trip-booking/trip-date'
import { IdInput } from '../../../lib/types/utils'
import ActionButtons from '../../elements/action-buttons'
import PeriodBadge from '../../elements/period-badge'
import { TripBookingItem } from '../trip-bookings/trip-booking.ui'
import { CenterLockStatus } from './trip-date.utils'

interface TripFolderCreationProps {
  tripDates: TripDate[]
  isLoading?: boolean

  onCancel: () => void
  onSubmit: (tripBookingIds: number[]) => Promise<void>
}

const TripFolderCreation = ({
  tripDates,
  isLoading,
  onSubmit,
  onCancel
}: TripFolderCreationProps) => {
  const [selectedRows, setSelectedRows] = useState<
    DataTableSelectedData<IdInput<TripBookingTypes.Partial>>
  >({ isAllSelected: false, rows: [] })

  const commonTripDateInfo = useMemo(
    () => (tripDates.length > 0 ? tripDates.at(0) : undefined),
    [tripDates]
  )

  const tripBookings = TripBookingProperties.sortTripBookingsByTime(
    tripDates
      .map((tripDate) => tripDate.tripBookings)
      .flat()
      .filter(
        (tripBooking) =>
          !tripBooking.tripFolders?.some(
            (folder) => folder.directionality === commonTripDateInfo?.directionality
          )
      )
  )

  const startDate = useMemo(() => {
    return new Date(tripDates[0].date)
  }, [tripDates])

  const { data, isLoading: isLoadingPeriod } = useQuery({
    queryKey: ['trip-date-period', startDate],
    queryFn: () => {
      const formattedStartDate = DateFormatter.toDateFormatted(startDate)
      return getPeriodByAvailability({
        startDate: formattedStartDate,
        endDate: formattedStartDate
      })
    },
    enabled: !!startDate
  })

  const columns: Column<TripBookingTypes.Partial>[] = [
    {
      accessorKey: 'tripBooking',
      header: 'Réservations',
      cell: ({ row: { original } }) => {
        const tripBookingItemValues = {
          tripBooking: original,
          options: {
            showCost: false,
            simpleDetails: true,
            displayLabel: !!original.secondaryReservation
          }
        }

        return (
          <Badge
            variant="white"
            color={tailwindColors.white.hover}
            className="py-1 h-fit my-2"
            text={
              <div className="flex flex-col gap-1 max-w-md">
                <TripBookingItem type={'main'} {...tripBookingItemValues} />
                {original.secondaryReservation && (
                  <TripBookingItem type={'secondary'} {...tripBookingItemValues} />
                )}
              </div>
            }
          />
        )
      }
    },
    {
      accessorKey: 'dispatchedCenters',
      header: 'Centre',
      hidden: !tripBookings.some((tripBooking) => tripBooking.dispatchedCenters?.length),
      cell: ({ row: { original } }) => {
        const centers = original.dispatchedCenters ?? []

        return (
          <div className="flex flex-col gap-1 py-1">
            {centers.map((center) => (
              <div key={center.id} className="flex items-center gap-2">
                <span>{center.name}</span>
                <CenterLockStatus locked={center.locked} />
              </div>
            ))}
          </div>
        )
      }
    }
  ]

  const hasTripBookingUnlockedCenter = (tripBookingId: number) =>
    !!tripBookings
      .find((tripBooking) => tripBooking.id === tripBookingId)
      ?.dispatchedCenters?.some((center) => !center.locked)

  const handleSubmit = async () => {
    const selectedTripBookingIds = (
      selectedRows.isAllSelected
        ? tripBookings.map((row) => row.id)
        : selectedRows.rows.map((row) => row.id)
    ).filter((id) => !hasTripBookingUnlockedCenter(id))
    if (selectedTripBookingIds.length === 0) return

    await onSubmit(selectedTripBookingIds)
  }

  if (!commonTripDateInfo) {
    return (
      <div className="flex flex-col gap-4 p-4 pb-0">
        <div className="text-center">Aucun voyage sélectionné</div>
      </div>
    )
  }

  return (
    <div>
      <div className={'flex flex-col gap-4 p-4 pb-0'}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {startDate && (
              <PeriodBadge
                loading={isLoadingPeriod}
                variant="fushia"
                period={data}
                dateDisplay={startDate}
              />
            )}
            <Badge
              text={tripTypeMapping[commonTripDateInfo.trip.type].label}
              variant={tripTypeMapping[commonTripDateInfo.trip.type].variant}
            />
            <Badge
              text={tripStepTypeMapping[commonTripDateInfo.directionality].label}
              variant={tripStepTypeMapping[commonTripDateInfo.directionality].variant}
            />
          </div>
          <div className="flex flex-col gap-1">
            {tripDates.map(({ trip }, index) => (
              <Badge
                key={index}
                variant="white"
                color={tailwindColors.white.hover}
                text={TripProperties.getTripLabel(trip)}
              />
            ))}
          </div>
        </div>

        {tripBookings && tripBookings.length > 0 ? (
          <DataTable<TripBookingTypes.Partial>
            selectable
            disableHover
            data={tripBookings}
            rowCount={tripBookings.length}
            paginated={false}
            columns={columns}
            displayEmptyState={false}
            onSelectedDataChange={setSelectedRows}
            selectedData={selectedRows}
            disableRow={(row) => hasTripBookingUnlockedCenter(row.id)}
          />
        ) : (
          <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
            <Typography variant="secondary-body">Pas de réservation disponible</Typography>
          </div>
        )}
      </div>

      <ActionButtons isLoading={isLoading} onCancel={onCancel} onSubmit={handleSubmit} />
    </div>
  )
}

export default TripFolderCreation
